"use client";

import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { API_URL } from "@/lib/api";
import Reveal from "./Reveal";

const ROLES = [
  { value: "instructor", label: "Instructor" },
  { value: "instructional_designer", label: "Instructional designer" },
  { value: "lms_admin", label: "LMS administrator" },
  { value: "other", label: "Other" },
];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState("");

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "");
    setStatus("sending");
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/v1/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email,
          institution: String(data.get("institution") ?? "").trim() || null,
          role: data.get("role"),
          message: data.get("message"),
        }),
      });
      if (response.status === 429) {
        throw new Error("Too many messages from this network right now — please try again in an hour.");
      }
      if (!response.ok) {
        throw new Error("That didn't go through — check the fields and try again.");
      }
      setSentTo(email);
      setStatus("sent");
      form.reset();
    } catch (err) {
      setStatus("idle");
      setError(
        err instanceof TypeError
          ? "Couldn't reach the server — please try again in a moment."
          : (err as Error).message,
      );
    }
  };

  return (
    <section id="contact" className="scroll-mt-16 border-t border-line bg-surface">
      <div className="mx-auto max-w-6xl px-6 py-20 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          <Reveal>
            <p className="ld-eyebrow">Contact</p>
            <h2 className="ld-display mt-2 text-2xl font-bold sm:text-3xl">
              See it in your Canvas instance
            </h2>
            <p className="mt-4 max-w-md leading-relaxed text-muted">
              Tell us a little about your institution and what you teach. We&apos;ll
              set up a walkthrough with your own course material — imported live,
              questions answered with citations.
            </p>
            <p className="mt-4 text-sm text-faint">We read every message.</p>
          </Reveal>

          <Reveal index={1}>
            {status === "sent" ? (
              <div className="ld-card flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
                <CheckCircle2 className="h-8 w-8 text-[var(--ok)]" />
                <p className="ld-display text-lg font-bold">Message received</p>
                <p className="max-w-sm text-sm text-muted">
                  Thanks — we&apos;ll get back to you at{" "}
                  <span className="font-medium text-foreground">{sentTo}</span>.
                </p>
              </div>
            ) : (
              // suppressHydrationWarning on the form + fields: password managers
              // and form-fillers inject attributes into inputs before React
              // hydrates, which otherwise trips a client/server mismatch.
              <form onSubmit={onSubmit} className="ld-card space-y-4 p-6 sm:p-8" suppressHydrationWarning>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="font-medium">Name</span>
                    <input name="name" required maxLength={120} className="ld-field mt-1" suppressHydrationWarning />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium">Email</span>
                    <input
                      name="email"
                      type="email"
                      required
                      maxLength={255}
                      className="ld-field mt-1"
                      suppressHydrationWarning
                    />
                  </label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="font-medium">Institution</span>{" "}
                    <span className="text-faint">(optional)</span>
                    <input name="institution" maxLength={200} className="ld-field mt-1" suppressHydrationWarning />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium">I am a…</span>
                    <select name="role" defaultValue="instructor" className="ld-field mt-1" suppressHydrationWarning>
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="font-medium">Message</span>
                  <textarea
                    name="message"
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={4}
                    placeholder="What are you teaching, and what would you like to see?"
                    className="ld-field mt-1 resize-y"
                    suppressHydrationWarning
                  />
                </label>
                {error && (
                  <p className="text-sm text-danger" role="alert">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="ld-cta w-full justify-center !bg-[var(--ld-ink)] !text-white hover:!bg-[var(--ld-ink-2)] disabled:opacity-60 sm:w-auto"
                >
                  {status === "sending" ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
